import { z } from 'zod';
import { overlayKindSchema } from './kinds.js';
/**
 * overlay-web 한 인스턴스가 보고하는 미디어 언락 단계다.
 * 콘솔 집계 문구와 섞지 않는다. 배열이 타입·검증의 유일한 원천이다.
 */
export const OVERLAY_RUNTIME_STATUS_PHASES = ['probing', 'unlocked', 'locked'];
export const overlayRuntimeStatusPhaseSchema = z.enum(OVERLAY_RUNTIME_STATUS_PHASES);
/**
 * 여러 인스턴스를 접은 콘솔 표시 값이다.
 * `probing`은 여기에 없다. 단계는 인스턴스에만 두고, 집계는 소리 켜짐을 단정할 수 있을 때만 `audible`이다.
 */
export const OVERLAY_RUNTIME_STATUSES = ['disconnected', 'unknown', 'muted', 'audible'];
export const overlayRuntimeStatusSchema = z.enum(OVERLAY_RUNTIME_STATUSES);
/**
 * PubSub로 콘솔에 보내는 이벤트 종류다.
 * 재생 `event.added`와 이름을 겹치지 않게 `runtime.` 접두를 둔다.
 */
export const OVERLAY_RUNTIME_STATUS_EVENT_TYPES = ['runtime.status.changed'];
export const overlayRuntimeStatusEventTypeSchema = z.enum(OVERLAY_RUNTIME_STATUS_EVENT_TYPES);
/**
 * 런타임 상태 계약 판이다.
 * 숫자 닫힌 집합이라 Zod 3의 `z.enum`은 쓰지 않고 배열에서 `z.literal`을 펼친다.
 */
export const OVERLAY_RUNTIME_STATUS_SCHEMA_VERSIONS = [1];
const overlayRuntimeStatusSchemaVersionLiterals = OVERLAY_RUNTIME_STATUS_SCHEMA_VERSIONS.map((version) => z.literal(version));
const overlayRuntimeStatusSchemaVersionFirst = overlayRuntimeStatusSchemaVersionLiterals[0];
if (overlayRuntimeStatusSchemaVersionFirst === undefined) {
    throw new Error('OVERLAY_RUNTIME_STATUS_SCHEMA_VERSIONS must contain at least one member');
}
export const overlayRuntimeStatusSchemaVersionSchema = overlayRuntimeStatusSchemaVersionFirst;
/**
 * 보고 주체 id의 최대 길이다.
 * Redis 문서 키에 붙이지 않지만, 한 문서 map 키가 커지는 것을 막는다.
 */
export const OVERLAY_RUNTIME_STATUS_INSTANCE_ID_MAX_LENGTH = 64;
/**
 * overlay-web이 만드는 불투명 id와 테스트용 짧은 id만 받는다.
 * 공백·경로 문자는 문서 map 키와 로그를 더럽히므로 거절한다.
 */
export const overlayRuntimeStatusInstanceIdSchema = z
    .string()
    .min(1)
    .max(OVERLAY_RUNTIME_STATUS_INSTANCE_ID_MAX_LENGTH)
    .regex(/^[A-Za-z0-9_-]+$/);
/**
 * overlay-web이 overlay-api에 보내는 보고 본문이다.
 * `kind`는 경로 파라미터다. `userId`·토큰·URL은 본문에 두면 다른 사용자 상태를 쓸 수 있어 빼는다.
 */
export const overlayRuntimeStatusReportSchema = z
    .object({
    schemaVersion: overlayRuntimeStatusSchemaVersionSchema,
    instanceId: overlayRuntimeStatusInstanceIdSchema,
    phase: overlayRuntimeStatusPhaseSchema,
    observedAt: z.string().datetime(),
})
    .strict();
/**
 * snapshot에 실리는 한 인스턴스다.
 * `expiresAt`은 서버가 채운 lease 만료다. 클라이언트가 고르지 않는다.
 */
export const overlayRuntimeStatusInstanceSchema = z
    .object({
    instanceId: overlayRuntimeStatusInstanceIdSchema,
    phase: overlayRuntimeStatusPhaseSchema,
    observedAt: z.string().min(1),
    expiresAt: z.string().min(1),
})
    .strict();
/**
 * CacheStore에 넣는 사용자·kind 문서다.
 * HTTP snapshot과 달리 인스턴스를 map으로 두어 heartbeat가 같은 instanceId를 덮는다.
 * 알 수 없는 키는 버리고, 필수 필드가 없으면 파싱을 실패시킨다.
 */
export const overlayRuntimeStatusDocumentSchema = z.object({
    revision: z.number().int().nonnegative(),
    status: overlayRuntimeStatusSchema,
    instances: z.record(overlayRuntimeStatusInstanceSchema),
});
/**
 * 콘솔이 읽는 현재 집계다.
 * `instances`는 서버가 보관한 행이고, `status`는 같은 집계 함수의 결과여야 한다.
 * `revision`은 캐시 문서와 같아 이벤트 순서를 가린다.
 */
export const overlayRuntimeStatusSnapshotSchema = z
    .object({
    schemaVersion: overlayRuntimeStatusSchemaVersionSchema,
    kind: overlayKindSchema,
    observedAt: z.string().min(1),
    instances: z.array(overlayRuntimeStatusInstanceSchema),
    status: overlayRuntimeStatusSchema,
    revision: z.number().int().nonnegative(),
})
    .strict();
/**
 * Web PubSub로 보내는 상태 변경 이벤트다.
 * payload를 snapshot과 같게 두어 콘솔 reducer가 HTTP와 실시간 경로를 같이 먹는다.
 */
export const overlayRuntimeStatusEventSchema = z
    .object({
    type: overlayRuntimeStatusEventTypeSchema,
    payload: overlayRuntimeStatusSnapshotSchema,
})
    .strict();
//# sourceMappingURL=overlay-runtime-status.schema.js.map